import { Box, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useFormik } from "formik";
import AsyncSelectScroll from "../../../../../components/AsyncSelectScroll";
import Flex from "../../../../../components/Flex";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import Text from "../../../../../components/Text";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";

import {
  DEF_PAGINATION,
  DGT_Pagination,
  showToastOnApiCall,
  getXtenantId,
  transformMultiSelectBackEndVal,
} from "../../../../../utils/utils";
import * as Yup from "yup";
import MaskMultiSelectScroll from "../../../../../components/MaskMultiSelectScroll";
import { usePatientsList } from "../../../../../queries/use-get-patients";
import { useUpdatePatientFlag } from "../../../../../queries/flags/patientFlags/use-update-patient-flag";
import { useAddPatientFlag } from "../../../../../queries/flags/patientFlags/use-add-patient-flag";
import { useGetFlagList } from "../../../../../queries/flags/flags/use-get-flag-list";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";

const UpsertPatientFlag = ({
  disclosureHost,
  refetch,
}: {
  disclosureHost: DisclosureHost;
  refetch?: () => void;
}) => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: updatePatientFlag } = useUpdatePatientFlag();
  const { mutateAsync: addPatientFlag } = useAddPatientFlag();

  const formik = useFormik({
    initialValues: disclosureHost.data || {
      flag: undefined,
      patients: undefined,
    },
    validationSchema: Yup.object({
      flag: Yup.object().required("Flag is required"),
      patients: Yup.array().of(Yup.object().required("Patient is required")),
    }),
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  async function handleSubmit(values?: any) {
    const selectedPatients = transformMultiSelectBackEndVal(values.patients);
    const selectedFlag = values.flag
      ? {
          [values.flag.value]: `${values.flag.label}`,
        }
      : undefined;

    const payload = {
      patientFlags: {
        uuid: disclosureHost.data?.uuid,
        patient: selectedPatients,
        flags: selectedFlag,
      },
      xTENANTID: getXtenantId(),
    };

    if (disclosureHost.data) showToastAndResetForm(updatePatientFlag(payload));
    else showToastAndResetForm(addPatientFlag(payload));
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    showToastOnApiCall({
      apiCall,
      showSnackbar,
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["patient-flag-list"],
        });
        closeAndResetForm();
      },
    });
  }

  function closeAndResetForm() {
    formik.resetForm();
    disclosureHost.close();
    refetch?.();
  }

  const [searchFlag, setFlagSearch] = useDebounce<string>("");
  const [patientSearchKey, setPatientSearchKey] = useDebounce<string>("");
  const [flagPagination, setFlagPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [patientPagination, setPatientPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: patientList, isFetching: patientFetching } = usePatientsList({
    searchKey: patientSearchKey,
    pagination: patientPagination,
    providerGroupId: getXtenantId(),
  });

  const patientOptions = useMemo(() => {
    const fetched =
      patientList?.data?.content?.map((it) => ({
        value: it?.uuid,
        label: it?.firstName + " " + it?.lastName,
      })) ?? [];
    const selected = (formik.values.patients ?? []) as {
      value: string;
      label: string;
    }[];
    const missing = selected.filter(
      (s) => !fetched.some((o) => o.value === s.value)
    );
    return [...missing, ...fetched];
  }, [patientList?.data?.content, formik.values.patients]);

  const handlePatientSearch = (val: string) => {
    setPatientSearchKey(val);
    setPatientPagination((p) => ({ ...p, page: 0 }));
  };
  const {
    data: flagList,
    isLoading: flagListLoading,
    isFetching: flagListFetching,
  } = useGetFlagList({
    pagination: flagPagination,
    xTENANTID: getXtenantId(),
    gridSorting: [],
    name: searchFlag,
    active: true,
    archive: false,
  });

  return (
    <SideSheetDrawer
      open={disclosureHost.opened}
      onClose={disclosureHost.close}
      title={
        disclosureHost.data ? "Edit Patient Flag" : "Assign Flag to Patient"
      }
      w="900px"
      footerActions={
        <>
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={disclosureHost.close}
            sx={{ mr: 2 }}
          />
          <StyledButtonV2
            label={"Save"}
            variant="contained"
            onClick={formik.submitForm}
          />
        </>
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2} gap={2}>
          {/* Patient */}
          <Grid item xs={12}>
            <MaskMultiSelectScroll
              onSearch={handlePatientSearch}
              optionWithCheckbox={true}
              value={formik.values.patients}
              placeholder="Patients"
              options={patientOptions}
              onChange={(val) => {
                formik.setFieldValue("patients", val);
              }}
              pagination={patientPagination}
              setPagination={setPatientPagination}
              totalElements={patientList?.data?.page?.totalElements}
              loading={patientFetching}
            />
          </Grid>
          <Grid item xs={12}>
            <AsyncSelectScroll
              required
              disabled={disclosureHost.data}
              loading={flagListFetching || flagListLoading}
              pagination={flagPagination}
              setPagination={setFlagPagination}
              totalElements={flagList?.data?.page?.totalElements}
              label="Flag"
              placeholder="Select Flag"
              enableFilterOption
              renderCustomLabel={(option) => (
                <Flex alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: "20px",
                      height: "15px",
                      backgroundColor: option.color,
                      borderRadius: "2px",
                      flexShrink: 0,
                    }}
                  />
                  <Text>{option.label}</Text>
                </Flex>
              )}
              renderSelectedValue={(option) => (
                <Flex alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: "20px",
                      height: "15px",
                      backgroundColor: option.color,
                      borderRadius: "2px",
                      flexShrink: 0,
                    }}
                  />
                </Flex>
              )}
              options={flagList?.data?.content?.map((it) => ({
                value: it?.uuid,
                label: it?.name,
                color: it?.color,
              }))}
              value={formik.values?.flag as any}
              onSearch={(val) => {
                setFlagSearch(val);
                setFlagPagination((p) => ({ ...p, page: 0 }));
              }}
              onChange={(val) => formik.setFieldValue("flag", val)}
              error={formik.touched.flag && (formik.errors.flag as any)}
            />
          </Grid>
        </Grid>
      </form>
    </SideSheetDrawer>
  );
};

export default UpsertPatientFlag;
